'use client';

import { blockKey } from '@hyphengroup/cms-components/calculatedAttributes/blockKey';
import type { PersonalLoanCalculationRequest } from '@hyphengroup/cms-components/calculatedAttributes/collect';
import {
  blockTravelQuote,
  CalculationStoreProvider,
  PAGE_SCOPE,
  type CalculationStore,
} from '@hyphengroup/cms-components/calculatedAttributes/store';
import { useCmsClientContext } from '@hyphengroup/cms-components/utils/cmsPageContext';
import { useSafeSearchParamsExtended } from '@hyphengroup/cms-components/utils/useSafeSearchParams';
import { useMemo, type ReactNode } from 'react';
import type { ModulePersonalLoanCalculations } from '../cmsService/pageDataService/modulePersonalLoanCalculations';
import { usePageDataContext } from '../cmsService/pageDataService/pageDataProvider';
import {
  useLoanRecalculation,
  type LoanCalculationJob,
  type LoanCalculations,
} from './useLoanRecalculation';
import { createPageCalculations, type LoanScopePlan } from './index';
import { PageLoanFilter } from '$/components/cms/components-internal/PersonalLoanFilter/PageLoanFilter';
import { flattenComponents } from '$/components/cms/helpers/componentTree';
import { useLoanSearchParams } from '$/components/cms/helpers/useLoanSearchParams';

type ByModule = Record<string, ModulePersonalLoanCalculations>;

/** A loan request with the block's position — what the plan and the refetch work on. */
type PositionedLoanRequest = PersonalLoanCalculationRequest & {
  position: number;
};

/** The server's results, signed with the terms the server planned. */
const fromServer = (
  calculations: ByModule | undefined,
  plan: LoanScopePlan,
): LoanCalculations => {
  const state: LoanCalculations = { byModule: {}, termsByModule: {} };
  for (const module of Object.values(plan.modules)) {
    const slice = calculations?.[module.moduleId];
    if (!slice) continue;
    state.byModule[module.moduleId] = slice;
    state.termsByModule[module.moduleId] = module.terms;
  }
  return state;
};

/**
 * Fills the calculation store for the page and keeps it in step with the
 * reader's filter (CMS-1890).
 *
 * The server calculated once, for the terms it could see. When the URL moves
 * the page scope, this re-plans and refetches the page-scope modules whose
 * terms changed — one call per set of terms, not one per module — and rewrites
 * their slots. Own-scope modules (a Table Builder on its stated loan) are never
 * refetched: nothing on the page moves them.
 *
 * Travel is carried through as the server left it: each block's slot holds its
 * own quote where it fetched one, else the page scope's — the quote the Travel
 * Policy Search drives — so an apply CTA anywhere on the page resolves the trip
 * its scope is priced for.
 */
export const PageCalculationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // The same context `PageDataProvider` itself requires — no second one.
  const { brand } = useCmsClientContext();
  const { pageData, isPreview: isDraft } = usePageDataContext();
  const { page } = pageData;
  const serverCalculations = pageData.modulePersonalLoanCalculations;

  const flattened = useMemo(
    () => flattenComponents(page.components),
    [page.components],
  );
  // The engine for this page: every block collected once, whatever the URL.
  const engine = useMemo(
    () => createPageCalculations({ brand, components: flattened, page }),
    [brand, flattened, page],
  );
  // Loan requests only: a block that asks for a travel quote has no products
  // to recalculate here, and its slot is never refetched by this provider.
  const requestById = useMemo(
    () =>
      new Map(
        engine.requests
          .filter(
            (request): request is PositionedLoanRequest =>
              request.provider === 'PL_CALCULATOR',
          )
          .map((request) => [request.moduleId, request]),
      ),
    [engine],
  );

  // What the server planned with: the same engine, minus the URL it could not
  // see. Derived rather than passed in, so the two cannot disagree.
  const serverPlan = engine.loans;

  const ready = !!useSafeSearchParamsExtended()?.searchParamsReady;
  // Only what the URL validly carries goes to the engine. A filled-in default
  // would read as "the reader said so" and beat a module's own starting value.
  const { fromUrl } = useLoanSearchParams({
    amount: String(serverPlan.pageTerms.amount),
    tenure: String(serverPlan.pageTerms.tenure),
  });

  const plan = useMemo(
    () => (ready ? engine.loansFor(fromUrl) : serverPlan),
    // The values, not the object: `useLoanSearchParams` rebuilds it on every
    // render. Same idiom as `PersonalLoanFilter`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, engine, fromUrl.amount, fromUrl.tenure, serverPlan],
  );

  // Which modules the reader's filter governs, and on what terms. Own-scope
  // modules (a Table Builder on its stated loan) are left out: nothing on the
  // page moves them.
  //
  // The server refuses the calculator on a brand with no Personal Loans
  // vertical — the market defaults would price a product set that does not
  // exist there — and says so by leaving the slice off entirely. The client
  // has no vertical list of its own, so it follows that answer instead of
  // re-deriving it; without this an editor authoring a PL module on money101
  // or moneymax armed a call the server had just declined.
  const jobs = useMemo<LoanCalculationJob[]>(() => {
    if (!serverCalculations) return [];
    return Object.values(plan.modules)
      .filter((module) => engine.blocks.get(module.moduleId)?.followsPageFilter)
      .map((module) => ({
        moduleId: module.moduleId,
        productIds: requestById.get(module.moduleId)?.criteria.productIds ?? [],
        terms: module.terms,
      }));
  }, [plan, engine, requestById, serverCalculations]);

  const loans = useLoanRecalculation({
    jobs,
    initial: useMemo(
      () => fromServer(serverCalculations, serverPlan),
      [serverCalculations, serverPlan],
    ),
    isDraft,
    enabled: ready,
  });

  // The loan terms are the plan's — the reader's choice — even while the
  // figures still show an earlier loan: an apply CTA must send the choice
  // (CMS-2052).
  const store = useMemo(() => {
    // The page's own slot, read by surfaces the host renders beside the block
    // mapper — the product aside modal, whose Apply CTA carries the reader's
    // trip and loan into the provider's link. They sit under no block, so
    // without this they would price against nothing.
    const value: CalculationStore = {
      [PAGE_SCOPE]: {
        travelQuote: pageData.travelQuotes,
        // Terms without figures: no page-level surface shows loan figures.
        personalLoan: { terms: plan.pageTerms },
      },
    };
    for (const component of flattened) {
      const id = blockKey(component);
      const terms = plan.modules[id]?.terms;
      value[id] = {
        vertical: engine.blocks.get(id)?.vertical,
        travelQuote: blockTravelQuote(id, {
          moduleTravelQuotes: pageData.moduleTravelQuotes,
          travelQuotes: pageData.travelQuotes,
        }),
        personalLoan: terms
          ? {
              terms,
              // None calculated still answers for a block that owns its loan
              // attributes: a dash, not the catalogue's value.
              calculations:
                loans.byModule[id] ??
                (engine.blocks.get(id)?.ownsLoanAttributes ? {} : undefined),
              followsPageFilter: engine.blocks.get(id)?.followsPageFilter,
            }
          : undefined,
      };
    }
    return value;
  }, [
    flattened,
    loans,
    plan,
    engine,
    pageData.moduleTravelQuotes,
    pageData.travelQuotes,
  ]);

  return (
    <CalculationStoreProvider value={store}>
      <PageLoanFilter
        page={page}
        components={flattened}
        terms={plan.pageTerms}
      />
      {children}
    </CalculationStoreProvider>
  );
};
